import { createSelector } from 'reselect';

import { formatBytes } from '@air/utilities';
import { isPdfRenderedExtension } from '@air/utils-clips';
import { getVideoFps } from '@air/utils-video';

import { PLACEHOLDER_IMAGE_BASE_NAME } from '~/components/ExportImagesModal/components/ExportImagesModalForm/components/ImageSuffixFormField';
import { centralizedBoardSelector } from '~/store/centralizedBoard/selectors';
import { type AirState } from '~/store/types';
import { isRiveFile } from '~/utils/RiveUtils';

const centralizedClipStateSelector = (state: AirState) => state.centralizedClip;

export const centralizedClipSelector = createSelector(centralizedClipStateSelector, ({ clip }) => clip);

export const centralizedClipIdSelector = createSelector(centralizedClipSelector, ({ id }) => id);

export const centralizedClipStatusSelector = createSelector(centralizedClipSelector, ({ status }) => status);

export const centralizedClipExtSelector = createSelector(centralizedClipSelector, ({ ext }) => ext);

export const centralizedClipAltResolutionsSelector = createSelector(
  centralizedClipSelector,
  ({ altResolutions }) => altResolutions,
);

export const centralizedClipDurationSelector = createSelector(centralizedClipSelector, ({ duration }) => duration);

export const centralizedClipFrameCountSelector = createSelector(
  centralizedClipSelector,
  ({ frameCount }) => frameCount,
);

export const centralizedDisplayTitleSelector = createSelector(
  centralizedClipSelector,
  ({ title, importedName }) => title || importedName || PLACEHOLDER_IMAGE_BASE_NAME,
);

export const centralizedClipDescriptionSelector = createSelector(
  centralizedClipSelector,
  ({ description }) => description,
);

export const centralizedClipIsDemoSelector = createSelector(centralizedClipSelector, ({ isDemo }) => isDemo);

export const centralizedClipSizeSelector = createSelector(centralizedClipSelector, ({ size }) => size);

export const centralizedClipFormattedSizeSelector = createSelector(centralizedClipSelector, ({ size }) =>
  formatBytes(size),
);

export const centralizedClipPDFAssetSelector = createSelector(centralizedClipSelector, ({ assets }) => assets.pdf);

export const centralizedClipPDFNumberOfPagesSelector = createSelector(
  centralizedClipSelector,
  ({ pdfPageNumber }) => pdfPageNumber,
);

export const centralizedClipDimensionsSelector = createSelector(centralizedClipSelector, ({ height, width }) => ({
  height,
  width,
}));

export const centralizedClipAssetsSelector = createSelector(centralizedClipSelector, ({ assets }) => assets);

export const centralizedClipVideoUrlSelector = createSelector(centralizedClipSelector, ({ assets }) => assets.video);

export const centralizedClipAssetIdSelector = createSelector(centralizedClipSelector, ({ assetId }) => assetId);

export const centralizedClipVTTUrlSelector = createSelector(
  centralizedClipSelector,
  ({ assets }) => assets.transcriptVtt,
);

export const centralizedClipSRTUrlSelector = createSelector(
  centralizedClipSelector,
  ({ assets }) => assets.transcriptSrt,
);

export const centralizedClipOpenDiscussionCountSelector = createSelector(
  centralizedClipSelector,
  ({ openDiscussionCount }) => openDiscussionCount,
);

export const centralizedClipIsLoadingSelector = createSelector(centralizedClipSelector, ({ id }) => !id);

export const centralizedClipTypeSelector = createSelector(centralizedClipSelector, ({ type }) => type);

export const centralizedClipIsPDFTypeSelector = createSelector(centralizedClipExtSelector, (ext) =>
  isPdfRenderedExtension(ext),
);

export const centralizedClipFPSSelector = createSelector(centralizedClipSelector, ({ frameCount, duration }) => {
  return getVideoFps({ frameCount, duration });
});

export const centralizedClipIsRiveSelector = createSelector(centralizedClipSelector, (clip) => isRiveFile(clip));

export const centralizedClipRecordedAtSelector = createSelector(
  centralizedClipSelector,
  ({ recordedAt }) => recordedAt,
);

export const centralizedClipUpdatedAtSelector = createSelector(centralizedClipSelector, ({ updatedAt }) => updatedAt);

export const centralizedClipSummarySelector = createSelector(centralizedClipSelector, ({ summary }) => summary);
export const centralizedClipKeywordsSelector = createSelector(centralizedClipSelector, ({ smartTags }) => smartTags);

export const centralizedClipTagsSelector = createSelector(centralizedClipSelector, ({ tags }) => tags);

export const centralizedClipCreatedDateSelector = createSelector(centralizedClipSelector, ({ createdAt }) => createdAt);

export const centralizedClipOwnerSelector = createSelector(centralizedClipSelector, ({ owner }) => owner);

export const centralizedClipSourceSelector = createSelector(centralizedClipSelector, ({ source }) => source);

export const centralizedClipBoardsSelector = createSelector(centralizedClipSelector, ({ boards }) => boards);

export const centralizedClipBoardContextSelector = createSelector(centralizedClipSelector, (clip) => clip.boardContext);

export const centralizedClipBoardInfoSelector = createSelector(
  [centralizedClipSelector, centralizedBoardSelector],
  ({ boards }, current) => {
    const currentBoard = boards.find((board) => board.id === current?.id);

    const others = current?.id ? boards.filter((board) => board.id !== current.id) : boards;

    return {
      others,
      current: !current?.id
        ? null
        : {
            ancestors: current.ancestors,
            id: current.id,
            library: current.library,
            openCommentCount: currentBoard?.openCommentCount,
            thumbnails: current.thumbnails,
            title: current.title,
          },
    };
  },
);

export const centralizedClipVersionCountSelector = createSelector(
  centralizedClipSelector,
  ({ assetVersionCount }) => assetVersionCount,
);

export const centralizedClipBookmarkedSelector = createSelector(
  centralizedClipSelector,
  ({ bookmarked }) => bookmarked,
);

export const centralizedClipVersionNumberSelector = createSelector(centralizedClipSelector, ({ version }) => version);

export const centralizedClipWorkspaceIdSelector = createSelector(
  centralizedClipSelector,
  ({ workspaceId }) => workspaceId,
);

// This is necessary for Cypress to mock methods, and must include all exported methods
export const CentralizedClipSelectors = {
  assetId: centralizedClipAssetIdSelector,
  clipId: centralizedClipIdSelector,
  displayTitle: centralizedDisplayTitleSelector,
};
